import axios from 'axios';
import Config from '../../../../config';
import Helpers from '../../../../helpers';
import { API_ENDPOINTS, FILE_TYPES, ERROR_LEVELS } from '../constants';
import OrderService from './OrderService';
import { BulkActionConfig, BulkActionOrder, FormattedUploadResults, TrackingResultItem } from '../types';

type BulkActionType = 'Retry' | 'Ignore' | 'Cancel';

class BulkActionService {
  /**
   * Upload tracking information from file
   * @param file - File to upload
   * @returns Upload results
   */
  async uploadTracking(file: File): Promise<TrackingResultItem[]> {
    try {
      if (!file) {
        throw new Error('No file provided');
      }

      // Determine file type
      let fileType: string = FILE_TYPES.CSV;
      if (file.name && file.name.endsWith('.xlsx')) {
        fileType = FILE_TYPES.XLSX;
      }

      // Prepare form data
      const formData = new FormData();
      formData.append('file', file);

      // Upload file
      const response = await axios.post(
        `${Config.api}${API_ENDPOINTS.TRACKING_UPLOAD}?fileType=${fileType}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      // Process results
      const results: TrackingResultItem[] = response.data.map((item: any, index: number) => ({
        id: index,
        ...item,
        Level: ERROR_LEVELS[item.Level] || ERROR_LEVELS[4]
      }));

      return results;
    } catch (error: any) {
      console.error('Error uploading tracking:', error);

      // Extract error message
      let errorMessage = 'An unexpected error occurred.';
      if (error.response && error.response.data) {
        const helpers = new Helpers();
        errorMessage = helpers.getApiErrors(error.response.data).join('\n');
      }

      throw new Error(errorMessage);
    }
  }

  /**
   * Retry failed orders
   * @param orders - Orders to retry
   */
  async retryOrders(orders: BulkActionOrder[]): Promise<void> {
    return OrderService.retryOrders(orders);
  }

  /**
   * Ignore failed orders
   * @param orders - Orders to ignore
   */
  async ignoreOrders(orders: BulkActionOrder[]): Promise<void> {
    return OrderService.ignoreOrders(orders);
  }

  /**
   * Cancel orders
   * @param orders - Orders to cancel
   */
  async cancelOrders(orders: BulkActionOrder[]): Promise<void> {
    return OrderService.cancelOrders(orders);
  }

  /**
   * Get bulk action configuration
   * @param action - Action type (Retry, Ignore, Cancel)
   * @param selectedOrders - Selected orders
   * @param selectedFailed - Number of failed orders
   * @returns Bulk action configuration
   */
  getBulkActionConfig(action: BulkActionType, selectedOrders: BulkActionOrder[], selectedFailed: number): BulkActionConfig | null {
    const configs: Record<BulkActionType, BulkActionConfig> = {
      'Retry': {
        action: 'Retry',
        target: selectedFailed,
        confirmText: 'retry',
        handler: () => this.retryOrders(selectedOrders)
      },
      'Ignore': {
        action: 'Ignore',
        target: selectedFailed,
        confirmText: 'ignore',
        handler: () => this.ignoreOrders(selectedOrders)
      },
      'Cancel': {
        action: 'Cancel',
        target: selectedOrders.length,
        confirmText: 'cancel',
        handler: () => this.cancelOrders(selectedOrders)
      }
    };

    return configs[action] || null;
  }

  /**
   * Validate file for upload
   * @param file - File to validate
   * @returns Whether file is valid
   */
  validateUploadFile(file: File | null | undefined): boolean {
    if (!file) return false;

    const validExtensions = ['.csv', '.xlsx', '.txt'];
    const fileName = file.name.toLowerCase();

    return validExtensions.some(ext => fileName.endsWith(ext));
  }

  /**
   * Format upload results for display
   * @param results - Upload results
   * @returns Formatted results with counts
   */
  formatUploadResults(results: TrackingResultItem[]): FormattedUploadResults {
    const counts = {
      success: 0,
      information: 0,
      warning: 0,
      error: 0,
      unknown: 0
    };

    results.forEach(result => {
      const level = (result.Level || 'Unknown').toLowerCase() as keyof typeof counts;
      if (counts.hasOwnProperty(level)) {
        counts[level]++;
      } else {
        counts.unknown++;
      }
    });

    return {
      results,
      counts,
      hasErrors: counts.error > 0,
      hasWarnings: counts.warning > 0,
      totalProcessed: results.length
    };
  }
}

const bulkActionService = new BulkActionService();
export default bulkActionService;